import React, { FC, HTMLAttributes } from "react";
import styled from "styled-components";
import { Box, Tab, TabContent } from "../TabView";
import ResolutionImg from "../image/ResolutionImg";
import { Breakable, CenterNote, CenterParagraph, ListItem, Note, UList } from "./Literals";
import DownloadButton from "./DownloadButton";

const CONCEPT_COLOR = "#0096B8";

export const OneStopTab: FC<HTMLAttributes<HTMLDivElement> & { active: boolean }> = ({ active, ...props }) => {
  return (
    <ColoredTab active={active} {...props}>
      <Breakable>ワンストップ特例制度を</Breakable>
      <Breakable>利用する場合</Breakable>
    </ColoredTab>
  );
};

const ColoredTab = styled(Tab)`
  color: ${({ active }) => (active ? "#FFFFFF" : "#1a1a1a")};
  background-color: ${({ active }) => (active ? CONCEPT_COLOR : "#CCCCCC")};
`;

const OneStopManual: FC<{ active: boolean }> = ({ active }) => {
  return (
    <Root active={active}>
      <BoxTarget subject="対象者" color={CONCEPT_COLOR}>
        <BoxInner>
          <StyledUList>
            <ListItem>
              年間の自治体への寄附が<span style={{ color: "#0700FF" }}>５箇所以内</span>の場合
            </ListItem>
            <ListItem>
              他に確定申告が必要な事案が<span style={{ color: "#0700FF" }}>ない</span>
            </ListItem>
          </StyledUList>
          <Note>
            同じ自治体であれば複数寄附しても１箇所として計算します。
            <br />
            <span style={{ textDecoration: "underline" }}>ただし、申し込みのたびに申請をする必要があります。</span>
          </Note>
        </BoxInner>
      </BoxTarget>
      <BoxSystemFlow>
        <BoxSystem subject="控除の仕組み" color={CONCEPT_COLOR}>
          <FlowImage pc="/flow/onestop/flow-pc.png" sp="/flow/onestop/flow-sp.png" loading="lazy" />
        </BoxSystem>
        <BoxFlow subject="ワンストップ特例制度の流れ" color={CONCEPT_COLOR}>
          <FlowImage pc="/flow/onestop/schedule-pc.png" sp="/flow/onestop/schedule-sp.png" loading="lazy" />
        </BoxFlow>
      </BoxSystemFlow>
      <BoxNecessity subject="申請に必要なもの" color={CONCEPT_COLOR}>
        <CenterParagraph>
          申請には２種類の書類を用意し、
          <br />
          期日までに寄附先の自治体に郵送する必要があります。
        </CenterParagraph>
        <CenterNote>同じ自治体でも申し込みのたびに申請が必要になります。</CenterNote>
        <CenterNote>郵送先は各プロジェクトページに記載されています。</CenterNote>
        <div>
          <DetailHead>①ワンストップ特例申請書</DetailHead>
          <DownloadArea>
            <NecessitiesImage1 pc="/flow/onestop/necessities-1-pc.png" sp="/flow/onestop/necessities-1-sp.png" loading="lazy" />
            <StyledDownloadButton />
          </DownloadArea>
          <CenterNote>申請用紙は返礼品とともに自治体から配送されることもあります。</CenterNote>
        </div>
        <div style={{ maxWidth: "670px", margin: "0 auto" }}>
          <DetailHead>②個人番号と本人の確認書類</DetailHead>
          <table style={{ border: "1px solid #1a1a1a", margin: "20px 0 5px" }}>
            <thead>
              <tr>
                <th style={{ backgroundColor: "#FDD7FF", width: "32%", border: "1px solid #1a1a1a" }}>A</th>
                <th style={{ backgroundColor: "#79E8D3", width: "32%", border: "1px solid #1a1a1a" }}>B</th>
                <th style={{ backgroundColor: "#EFD9BF", width: "32%", border: "1px solid #1a1a1a" }}>C</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Cell style={{ border: "1px solid #1a1a1a", borderBottom: "none" }}>
                  <Necessities image={{ pc: "/flow/onestop/necessities-2-pc.png", sp: "/flow/onestop/necessities-2-sp.png" }}>
                    「マイナンバーカード」のコピー（裏面）
                  </Necessities>
                </Cell>
                <Cell style={{ border: "1px solid #1a1a1a" }}>
                  <Necessities image={{ pc: "/flow/onestop/necessities-4-pc.png", sp: "/flow/onestop/necessities-4-sp.png" }}>
                    通知カードのコピー
                  </Necessities>
                </Cell>
                <Cell style={{ border: "1px solid #1a1a1a" }}>
                  <Necessities image={{ pc: "/flow/onestop/necessities-5-pc.png", sp: "/flow/onestop/necessities-5-sp.png" }}>
                    「個人番号入りの住民票」の写し
                  </Necessities>
                </Cell>
              </tr>
              <tr>
                <Cell style={{ borderTop: "none" }}>
                  <Necessities image={{ pc: "/flow/onestop/necessities-3-pc.png", sp: "/flow/onestop/necessities-3-sp.png" }}>
                    「マイナンバーカード」のコピー（表面）
                  </Necessities>
                </Cell>
                <Cell colSpan={2} style={{ border: "1px solid #1a1a1a", verticalAlign: "top", padding: "0" }}>
                  <div style={{ backgroundColor: "#EEE", padding: "5px" }}>
                    <p style={{ textAlign: "center" }}>下記いずれかの身分証のコピー</p>
                    <CenterNote>写真、氏名、生年月日、住所が必要になります。</CenterNote>
                  </div>
                  <StyledList>
                    <ListItem>運転免許証</ListItem>
                    <ListItem>運転経歴証明書</ListItem>
                    <ListItem>パスポート</ListItem>
                    <ListItem>公的医療保険の被保険者証（健康保険証）</ListItem>
                    <ListItem>身体障害者手帳</ListItem>
                    <ListItem>精神障害者保健福祉手帳</ListItem>
                    <ListItem>療育手帳</ListItem>
                    <ListItem>在留カード</ListItem>
                    <ListItem>特別永住者証明書</ListItem>
                  </StyledList>
                  <div style={{ borderTop: "1px dashed #1a1a1a", padding: "5px" }}>
                    <p style={{ textAlign: "center" }}>（写真付きの身分証がない場合）</p>
                    <CenterNote>下記いずれかの２つの写しの提出が必要になります。</CenterNote>
                  </div>
                  <StyledList>
                    <ListItem>健康保険証</ListItem>
                    <ListItem>年金手帳</ListItem>
                    <ListItem>寄附先の自治体が認める上記以外の確認書類</ListItem>
                  </StyledList>
                </Cell>
              </tr>
            </tbody>
          </table>
          <Note>A〜Cのいずれかを選択してください。</Note>
          <Note>(B)の場合、通知カードの住所と名前が住民票と一致している必要があります。</Note>
        </div>
      </BoxNecessity>
    </Root>
  );
};
export default OneStopManual;

const Necessities: FC<{ image: { pc: string; sp: string } }> = ({ image: { pc, sp }, children }) => {
  return (
    <NecessitiesRoot>
      <NecessitiesImage2 pc={pc} sp={sp} loading="lazy" />
      <div>{children}</div>
    </NecessitiesRoot>
  );
};

const StyledList = styled.ul`
  padding: 5px;
`;

const Root = styled(TabContent)`
  border: 2px solid ${CONCEPT_COLOR};
  border-radius: 0 0 20px 20px;
  @media screen and (min-width: 961px) {
    padding: 20px 30px;
    display: ${({ active }) => (active ? "flex" : "none")};
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: stretch;
  }
`;
const StyledBox = styled(Box)`
  @media screen and (max-width: 960px) {
    margin-bottom: 30px;
  }
  @media screen and (min-width: 961px) {
    margin-bottom: 40px;
  }
`;
const BoxTarget = styled(StyledBox)`
  margin-top: 20px;
  @media screen and (min-width: 961px) {
    flex-basis: 100%;
  }
`;
const BoxSystemFlow = styled.div`
  @media screen and (min-width: 961px) {
    flex-basis: 49%;
    display: flex;
    flex-direction: column;
  }
`;
const BoxSystem = styled(StyledBox)`
  flex-grow: 1;
`;
const BoxFlow = styled(StyledBox)`
  flex-grow: 1;
`;
const BoxNecessity = styled(StyledBox)`
  @media screen and (min-width: 961px) {
    flex-basis: 49%;
  }
`;
const BoxInner = styled.div`
  @media screen and (min-width: 961px) {
    width: 420px;
    margin: 0 auto;
  }
`;
const FlowImage = styled(ResolutionImg)`
  width: 100%;
  @media screen and (max-width: 960px) {
  }
  @media screen and (min-width: 601px) and (max-width: 960px) {
    max-width: 400px;
    margin: 0 auto;
    display: block;
  }
  @media screen and (min-width: 961px) {
    padding: 10px;
  }
`;
const NecessitiesRoot = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
`;
const NecessitiesImage1 = styled(ResolutionImg)`
  width: 120px;
`;
const NecessitiesImage2 = styled(ResolutionImg)`
  max-width: 60px;
  max-height: 60px;
  margin: 0 auto 10px;
`;

const StyledUList = styled(UList)`
  margin: 0 0 10px;
`;
const DetailHead = styled.h6`
  margin-top: 20px;
  font-size: 1em;
  text-align: center;
`;
const DownloadArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  //width: 260px;
  margin: 20px auto;
`;
const StyledDownloadButton = styled(DownloadButton)`
  margin-left: 10px;
`;
const Cell = styled.td`
  padding: 10px;
  font-size: 10px;
  vertical-align: middle;
  @media screen and (min-width: 961px) {
    font-size: 12px;
  }
  @media screen and (min-width: 1441px) {
    font-size: 14px;
  }
`;
